import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { resetConsent } from "@/components/ConsentBanner";

export default function Cookies() {
  const page = webPageSchema({
    path: "/cookies",
    title: "Cookies & Analytics Policy | Shariq Refai, MD, MBA",
    description:
      "How shariqrefai.com uses Google Analytics 4 with strict consent, which cookies are set, retention, and how to change or withdraw consent.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Cookies & Analytics", path: "/cookies" },
    ],
  });

  const handleReset = () => {
    resetConsent();
    // Reload so the banner reappears fresh.
    if (typeof window !== "undefined") window.location.reload();
  };

  return (
    <SiteLayout>
      <SEO
        title="Cookies & Analytics Policy | Shariq Refai, MD, MBA"
        description="How shariqrefai.com uses Google Analytics 4 with strict consent, which cookies are set, retention, and how to change or withdraw consent."
        url="/cookies"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Cookies", path: "/cookies" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Cookies</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Cookies &amp; Analytics
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            Plain-language overview of what this website stores in your browser
            and how to change or withdraw consent.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: July 16, 2026.</strong>
          </p>

          <h2>What this site uses</h2>
          <p>
            shariqrefai.com uses <strong>Google Analytics 4</strong> (measurement
            ID <code>G-P568VWDRRY</code>) to understand aggregate traffic:
            which pages get read, roughly where readers are in the world, and
            how people move around the site. That's it. This site does not run
            advertising cookies, does not build profiles, does not sell or
            share personal information, and does not use analytics for any
            clinical decision.
          </p>
          <p>
            Google Analytics is loaded with{" "}
            <strong>Google Consent Mode v2</strong> in strict mode. Nothing is
            written to your browser and nothing is loaded from Google's servers
            until you actively accept on the consent banner. If you decline, or
            never respond, no analytics cookies are set and no analytics
            requests are sent.
          </p>

          <h2>Cookies set only after you accept</h2>
          <p>The following are set only after you click Accept analytics:</p>
          <ul className="list-disc pl-6">
            <li>
              <code>_ga</code> — assigns a random visitor ID so returning visits
              can be counted as one person rather than two. Expires after 2
              years.
            </li>
            <li>
              <code>_ga_P568VWDRRY</code> — session state cookie tied to this
              specific GA4 property. Expires after 2 years.
            </li>
            <li>
              <code>consent_v1</code> — first-party localStorage entry that
              remembers your Accept or Decline choice so the banner does not
              reappear on every page. This is set as soon as you make a choice,
              accept or decline, and never leaves your browser.
            </li>
          </ul>
          <p>
            IP addresses are truncated by Google before storage
            (<code>anonymize_ip</code> is enabled), advertising features and
            Google Signals are disabled, and no information collected here is
            used to build advertising audiences. Data is processed by Google
            LLC under its{" "}
            <a
              href="https://policies.google.com/technologies/partner-sites"
              target="_blank"
              rel="noopener noreferrer"
            >
              Analytics data processing terms
            </a>
            .
          </p>

          <h2>Retention</h2>
          <p>
            Event-level and user-level analytics data is retained by Google for
            up to 14 months and then automatically deleted. Aggregate reports
            (which do not identify individuals) may be retained longer.
          </p>

          <h2>Signals that opt you out automatically</h2>
          <p>
            The banner will not appear, and analytics will remain off, if any
            of the following are true:
          </p>
          <ul className="list-disc pl-6">
            <li>Your browser sends a Global Privacy Control (GPC) signal.</li>
            <li>Your browser sends the legacy Do Not Track (DNT) header.</li>
            <li>
              Your session is a prerender, headless browser, or a known
              search-engine or link-preview crawler.
            </li>
          </ul>

          <h2>Change or withdraw your consent</h2>
          <p>
            You can change your choice at any time. Clicking the button below
            clears the stored decision and reloads the page, so the banner
            appears again fresh.
          </p>
          <p>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex h-11 items-center justify-center bg-primary px-6 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent no-underline"
            >
              Reset my analytics consent
            </button>
          </p>
          <p>
            You can also delete Google Analytics cookies through your browser's
            site data settings, or install the{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Analytics Opt-out Browser Add-on
            </a>{" "}
            to block GA on any site.
          </p>

          <h2>What Cookies Are</h2>
          <p>
            Cookies are small text files placed on your device by the websites
            you visit. They are widely used to make websites work, to remember
            preferences, and to provide aggregate information about how a site
            is being used.
          </p>

          <h2>Your Browser Controls</h2>
          <p>
            Most browsers let you view, manage, block, or delete cookies and
            site data through their settings. You can visit{" "}
            <a
              href="https://www.allaboutcookies.org/"
              target="_blank"
              rel="noopener noreferrer"
            >
              allaboutcookies.org
            </a>{" "}
            for general guidance.
          </p>

          <h2>Global Privacy Control</h2>
          <p>
            shariqrefai.com honors Global Privacy Control (GPC) as a
            browser-level opt-out signal for the purposes of the California
            Consumer Privacy Act (CCPA/CPRA) and comparable state laws. When
            GPC is present, analytics stays off and no consent banner is
            shown.
          </p>

          <h2>Changes</h2>
          <p>
            This page may be updated from time to time as the website's tooling
            changes. Material changes to the disclosures here will also cause
            the stored consent version to be bumped, so you'll be asked again
            rather than assumed to have consented to something new.
          </p>

          <h2>Related</h2>
          <p>
            For more on how information is handled overall, see the{" "}
            <Link href="/privacy">Privacy Policy</Link>. Questions can be sent
            through the <Link href="/contact">contact page</Link> or to{" "}
            <a href="mailto:privacy@shariqrefai.com">privacy@shariqrefai.com</a>.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
